import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Plus, Search, MoreHorizontal, Trash2, Smartphone, Signal } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusLabels = { attiva: 'Attiva', sospesa: 'Sospesa', cessata: 'Cessata' };

const emptySIM = {
  contract_id: '',
  client_id: '',
  phone_number: '',
  sim_serial: '',
  tariff_plan: '',
  data_gb: '',
  minutes: '',
  sms: '',
  status: 'attiva'
};

const TelecomSIMs = () => {
  const [sims, setSims] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState(emptySIM);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [simsRes, contractsRes, clientsRes] = await Promise.all([
        axios.get(`${API}/telecom/sims`),
        axios.get(`${API}/telecom/contracts`),
        axios.get(`${API}/clients`)
      ]);
      setSims(simsRes.data);
      setContracts(contractsRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        data_gb: formData.data_gb ? parseFloat(formData.data_gb) : 0,
        minutes: formData.minutes ? parseInt(formData.minutes) : 0,
        sms: formData.sms ? parseInt(formData.sms) : 0
      };
      await axios.post(`${API}/telecom/sims`, payload);
      toast.success('SIM creata');
      setDialogOpen(false);
      setFormData(emptySIM);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questa SIM?')) return;
    try {
      await axios.delete(`${API}/telecom/sims/${id}`);
      toast.success('SIM eliminata');
      fetchData();
    } catch (error) {
      toast.error('Errore');
    }
  };

  const handleContractChange = (contractId) => {
    const contract = contracts.find(c => c.id === contractId);
    setFormData({
      ...formData,
      contract_id: contractId,
      client_id: contract?.client_id || ''
    });
  };

  const filteredSims = sims.filter(s =>
    `${s.client_name} ${s.phone_number} ${s.sim_serial}`.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusBadge = (status) => {
    const classes = { attiva: 'bg-emerald-100 text-emerald-700', sospesa: 'bg-amber-100 text-amber-700', cessata: 'bg-slate-100 text-slate-700' };
    return <Badge className={classes[status]}>{statusLabels[status]}</Badge>;
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="telecom-sims-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>SIM</h1>
          <p className="text-slate-600 mt-1">Gestisci le SIM e i numeri di telefono</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-violet-600 hover:bg-violet-700 text-white" onClick={() => setFormData(emptySIM)} data-testid="add-sim-btn">
              <Plus className="w-4 h-4 mr-2" />Nuova SIM
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Nuova SIM</DialogTitle>
              <DialogDescription>Inserisci i dati della SIM</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Contratto *</Label>
                <Select value={formData.contract_id} onValueChange={handleContractChange}>
                  <SelectTrigger data-testid="sim-contract-select"><SelectValue placeholder="Seleziona contratto" /></SelectTrigger>
                  <SelectContent>
                    {contracts.filter(c => c.status === 'attivo').map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.contract_number} - {c.client_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Numero Telefono *</Label>
                  <Input value={formData.phone_number} onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })} placeholder="3xx xxxxxxx" required data-testid="sim-phone" />
                </div>
                <div className="space-y-2">
                  <Label>Seriale SIM *</Label>
                  <Input value={formData.sim_serial} onChange={(e) => setFormData({ ...formData, sim_serial: e.target.value })} required data-testid="sim-serial" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Piano Tariffario *</Label>
                <Input value={formData.tariff_plan} onChange={(e) => setFormData({ ...formData, tariff_plan: e.target.value })} placeholder="Es: TIM Young, Vodafone Infinito..." required data-testid="sim-plan" />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Dati (GB)</Label>
                  <Input type="number" value={formData.data_gb} onChange={(e) => setFormData({ ...formData, data_gb: e.target.value })} data-testid="sim-data" />
                </div>
                <div className="space-y-2">
                  <Label>Minuti</Label>
                  <Input type="number" value={formData.minutes} onChange={(e) => setFormData({ ...formData, minutes: e.target.value })} data-testid="sim-minutes" />
                </div>
                <div className="space-y-2">
                  <Label>SMS</Label>
                  <Input type="number" value={formData.sms} onChange={(e) => setFormData({ ...formData, sms: e.target.value })} data-testid="sim-sms" />
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Annulla</Button>
                <Button type="submit" className="bg-violet-600 hover:bg-violet-700" data-testid="save-sim-btn">Crea SIM</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder="Cerca per numero, seriale, cliente..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" data-testid="search-sims" />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-slate-500">Caricamento...</div>
          ) : filteredSims.length === 0 ? (
            <div className="p-8 text-center text-slate-500">Nessuna SIM trovata</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>SIM</TableHead>
                    <TableHead>Cliente</TableHead>
                    <TableHead>Piano</TableHead>
                    <TableHead>Dati</TableHead>
                    <TableHead>Stato</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredSims.map((sim) => (
                    <TableRow key={sim.id} data-testid={`sim-row-${sim.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
                            <Smartphone className="w-5 h-5 text-violet-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{sim.phone_number}</p>
                            <p className="text-sm text-slate-500 font-mono">{sim.sim_serial}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <p className="font-medium">{sim.client_name}</p>
                        <p className="text-sm text-slate-500">{sim.contract_number}</p>
                      </TableCell>
                      <TableCell className="font-medium">{sim.tariff_plan}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-sm">
                          <Signal className="w-3 h-3 text-violet-500" />
                          {sim.data_gb > 0 ? `${sim.data_gb} GB` : 'Illimitati'}
                        </div>
                      </TableCell>
                      <TableCell>{getStatusBadge(sim.status)}</TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon"><MoreHorizontal className="w-4 h-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleDelete(sim.id)} className="text-red-600"><Trash2 className="w-4 h-4 mr-2" />Elimina</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TelecomSIMs;
